import { useMemo, useState } from 'react';
import { Icon } from '../ui/Icon';
import { logoUrl } from '../api/client';
import { TRACKER_IDS, TRACKER_NAMES, type CasinoRow, type Payload, type TrackerId, type WindowKey } from '../api/types';
import { gapStep, pct, usd } from '../lib/format';
import { Rail, finite } from '../ui/charts';
import { Scatter } from '../ui/compare';
import { CardHead, GlassColumns, GlassHBars } from '../ui/glass';
import { Logo } from '../ui/Logo';
import { CompareAll } from './CompareAll';
import { openRowLink } from '../ui/rowLink';

/** Three views: all four trackers against each other, two trackers casino by casino, or up to six casinos side by side. */
export function Compare({ data, win, mode, search }: { data: Payload; win: WindowKey; mode: 'all' | 'pair' | 'casinos'; search: URLSearchParams }) {
  const a = data.analysis.volume[win];
  const intro = {
    all: 'The four trackers against each other: what each covers, who reads high or low, how often each pair agrees, and what each one publishes.',
    pair: 'Two trackers, the casinos they both list, and how closely their figures line up casino by casino.',
    casinos: 'Up to six casinos, every tracker’s figure for each, side by side.',
  }[mode];
  return (
    <div className="page">
      <section className="tile banner-head banner-compare">
        <div className="page-head">
          <div>
            <h1>Compare</h1>
            <p>{intro}</p>
          </div>
          <nav className="seg" aria-label="Comparison">
            <a href={`/compare?w=${win}`} aria-current={mode === 'all' ? 'page' : undefined}>All four</a>
            <a href={`/pair?w=${win}`} aria-current={mode === 'pair' ? 'page' : undefined}>Two trackers</a>
            <a href={`/matchup?w=${win}`} aria-current={mode === 'casinos' ? 'page' : undefined}>Casinos side by side</a>
          </nav>
        </div>
      </section>
      {mode === 'all' ? <CompareAll data={data} win={win} />
        : !a ? <div className="tile"><p className="empty">Nothing published for this window.</p></div>
        : mode === 'pair' ? <Trackers key={`${search.get('x')}-${search.get('y')}`} rows={a.rows} win={win} counted={a.consensusSources} initial={[search.get('x'), search.get('y')]} />
        : <Casinos key={search.get('c') ?? ''} rows={a.rows} win={win} pin={search.get('c')} />}
    </div>
  );
}

/* ---------------------------------------------------------- two trackers */

const BINS = [
  { lo: 0, hi: 0.5, label: 'under half' },
  { lo: 0.5, hi: 0.8, label: '50–80%' },
  { lo: 0.8, hi: 0.95, label: '80–95%' },
  { lo: 0.95, hi: 1.05, label: 'within 5%' },
  { lo: 1.05, hi: 1.25, label: '105–125%' },
  { lo: 1.25, hi: 2, label: '125–200%' },
  { lo: 2, hi: Infinity, label: 'over double' },
];

const isTracker = (v: string | null): v is TrackerId => (TRACKER_IDS as readonly string[]).includes(v ?? '');

function Trackers({ rows, win, counted, initial }: { rows: CasinoRow[]; win: WindowKey; counted: TrackerId[]; initial: [string | null, string | null] }) {
  const listed = TRACKER_IDS.filter((id) => rows.some((r) => finite(r.by[id]?.value)));
  // A pair can arrive in the URL (from the agreement grid on "All four").
  const [pick, setPick] = useState<[TrackerId, TrackerId]>(() => {
    const [ix, iy] = initial;
    if (isTracker(ix) && isTracker(iy) && ix !== iy && listed.includes(ix) && listed.includes(iy)) return [ix, iy];
    const pool = counted.length >= 2 ? counted : listed;
    return [pool[0] as TrackerId, (pool[1] ?? pool[0]) as TrackerId];
  });
  const [x, y] = pick;
  const set = (slot: 0 | 1, id: TrackerId) => setPick(slot === 0 ? [id, id === y ? x : y] : [id === x ? y : x, id]);

  const pairs = rows.flatMap((r) => {
    const vx = r.by[x]?.value;
    const vy = r.by[y]?.value;
    return finite(vx) && finite(vy) && vx > 0 && vy > 0 ? [{ row: r, vx, vy, ratio: vy / vx }] : [];
  });
  const totX = pairs.reduce((s, p) => s + p.vx, 0);
  const totY = pairs.reduce((s, p) => s + p.vy, 0);
  const sortedRatios = pairs.map((p) => p.ratio).sort((m, n) => m - n);
  const medRatio = sortedRatios.length ? sortedRatios[Math.floor(sortedRatios.length / 2)] ?? null : null;
  const close = pairs.filter((p) => p.ratio >= 0.9 && p.ratio <= 1.1).length;
  const bins = BINS.map((b) => ({ ...b, n: pairs.filter((p) => p.ratio >= b.lo && p.ratio < b.hi).length }));
  const worst = [...pairs].sort((m, n) => Math.abs(Math.log(n.ratio)) - Math.abs(Math.log(m.ratio))).slice(0, 25);

  const Picker = ({ slot, value }: { slot: 0 | 1; value: TrackerId }) => (
    <div className="seg" role="group" aria-label={slot === 0 ? 'First tracker' : 'Second tracker'}>
      {listed.map((id) => <button key={id} aria-pressed={id === value} onClick={() => set(slot, id)}>{TRACKER_NAMES[id]}</button>)}
    </div>
  );

  return (
    <>
      <section className="tile">
        <div className="versus">
          <Picker slot={0} value={x} />
          <span className="versus-mark">against</span>
          <Picker slot={1} value={y} />
        </div>
        <div className="kpis">
          <div className="kpi"><small>Casinos both list</small><b>{pairs.length}</b><span>the only fair comparison</span></div>
          <div className="kpi"><small><i className="swatch" style={{ background: `var(--tracker-${x})` }} aria-hidden />{TRACKER_NAMES[x]}</small><b>{usd(totX)}</b><span>over those casinos</span></div>
          <div className="kpi"><small><i className="swatch" style={{ background: `var(--tracker-${y})` }} aria-hidden />{TRACKER_NAMES[y]}</small><b>{usd(totY)}</b><span>over those casinos</span></div>
          <div className="kpi accent"><small>Within 10% of each other</small><b>{pairs.length ? pct((close / pairs.length) * 100, 0) : '—'}</b><span>{close} of {pairs.length} casinos</span></div>
        </div>
      </section>

      <div className="pgrid">
        <section className="tile span-7">
          <CardHead icon="line" tone="blue" title="Casino by casino" note={`One dot per casino both list. On the diagonal they agree; above it ${TRACKER_NAMES[y]} reports more.`} />
          <Scatter
            points={pairs.map((p) => ({ key: p.row.key, name: p.row.name, x: p.vx, y: p.vy, href: `/casino?c=${p.row.key}&w=${win}` }))}
            xName={TRACKER_NAMES[x]} yName={TRACKER_NAMES[y]} xColor={`var(--tracker-${x})`} yColor={`var(--tracker-${y})`} format={usd}
          />
          <div className="ramp-key scatter-key" aria-hidden>
            <span><i className="t0" />within 10%</span><span><i className="t1" />10–25% apart</span><span><i className="t2" />up to double</span><span><i className="t3" />more than double</span>
          </div>
        </section>
        <section className="tile span-5">
          <div className="tile-head">
            <h2 className="tile-title">How the figures line up</h2>
            <span className="tile-note">{TRACKER_NAMES[y]} as a share of {TRACKER_NAMES[x]}</span>
          </div>
          <GlassHBars items={bins.map((b) => ({
            key: b.label, label: b.label, value: b.n, display: `${b.n}`,
            className: b.lo >= 0.95 && b.hi <= 1.05 ? '' : `g${b.lo >= 0.8 && b.hi <= 1.25 ? 1 : b.lo >= 0.5 && b.hi <= 2 ? 2 : 3}`,
          }))} />
          <p className="tile-foot">
            Typical casino: {TRACKER_NAMES[y]} reports {finite(medRatio) ? `${(medRatio * 100).toFixed(0)}%` : '—'} of what {TRACKER_NAMES[x]} reports.
            The blue bar is agreement; the further a bar sits from it, the further apart the two trackers are.
          </p>
        </section>

        <section className="tile span-12">
          <div className="tile-head"><h2 className="tile-title">Furthest apart</h2><span className="tile-note">by ratio, either direction</span></div>
          <div className="t-wrap t-short">
            <table className="t">
              <thead><tr><th>Casino</th><th className="r">{TRACKER_NAMES[x]}</th><th className="r">{TRACKER_NAMES[y]}</th><th className="r">Ratio</th></tr></thead>
              <tbody>
                {worst.map((p) => (
                  <tr key={p.row.key} className="row-link" onClick={openRowLink}>
                    <td><a className="plink" href={`/casino?c=${p.row.key}&w=${win}`}>{p.row.name}</a></td>
                    <td className="r">{usd(p.vx)}</td>
                    <td className="r">{usd(p.vy)}</td>
                    <td className="r"><span className={`gap-pill g${gapStep(Math.abs(p.ratio - 1) * 100)}`}>{p.ratio >= 10 ? `${p.ratio.toFixed(0)}×` : `${p.ratio.toFixed(2)}×`}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
}

/* ------------------------------------------------------ casinos side by side */

function Casinos({ rows, win, pin }: { rows: CasinoRow[]; win: WindowKey; pin: string | null }) {
  const ranked = useMemo(() => [...rows].filter((r) => finite(r.consensus)).sort((m, n) => (n.consensus ?? 0) - (m.consensus ?? 0)), [rows]);
  // Arriving from a casino's profile (?c=key) puts that casino first, beside the three largest others.
  const [keys, setKeys] = useState<string[]>(() => {
    const top = ranked.map((r) => r.key);
    return pin && rows.some((r) => r.key === pin) ? [pin, ...top.filter((k) => k !== pin).slice(0, 3)] : top.slice(0, 4);
  });
  const [q, setQ] = useState('');
  const chosen = keys.flatMap((k) => rows.filter((r) => r.key === k));
  const matches = q.trim()
    ? rows.filter((r) => r.name.toLowerCase().includes(q.trim().toLowerCase()) && !keys.includes(r.key)).slice(0, 6)
    : [];
  const trackers = TRACKER_IDS.filter((id) => chosen.some((r) => finite(r.by[id]?.value)));

  return (
    <>
      <section className="tile">
        <div className="toolbar">
          <label className="search">
            <Icon name="search" size={18} />
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={keys.length >= 6 ? 'Six is the limit: remove one first' : 'Add a casino'} disabled={keys.length >= 6} aria-label="Add a casino" />
          </label>
          <div className="picked">
            {chosen.map((r) => (
              <button key={r.key} className="chip" onClick={() => setKeys((k) => k.filter((x) => x !== r.key))} aria-label={`Remove ${r.name}`}>
                {r.name} <span aria-hidden>×</span>
              </button>
            ))}
          </div>
        </div>
        {matches.length > 0 && (
          <div className="suggest">
            {matches.map((r) => (
              <button key={r.key} className="chip" onClick={() => { setKeys((k) => [...k, r.key].slice(0, 6)); setQ(''); }}>
                + {r.name} <span>{usd(r.consensus)}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {chosen.length === 0 ? <div className="tile"><p className="empty">Add a casino to start.</p></div> : (
        <>
          <div className="matchup">
            {chosen.map((r) => {
              const readings = TRACKER_IDS.flatMap((id) => {
                const v = r.by[id]?.value;
                return finite(v) ? [{ id, value: v, counted: r.consensusBy.includes(id) }] : [];
              });
              const logo = r.logos && Object.keys(r.logos).length ? logoUrl(r.key) : null;
              return (
                <a key={r.key} className="tile match-card" href={`/casino?c=${r.key}&w=${win}`}>
                  <span className="match-id"><Logo src={logo} name={r.name} className="casino-blank" /> {r.name}</span>
                  <b>{usd(r.consensus)}</b>
                  <small>median deposits</small>
                  {finite(r.spreadPct) ? <span className={`gap-pill g${gapStep(r.spreadPct)}`}>{pct(r.spreadPct, 0)} apart</span> : <span className="chip">one source</span>}
                  {readings.length > 1 && <Rail readings={readings} compact />}
                </a>
              );
            })}
          </div>

          <section className="tile">
            <CardHead icon="compare" tone="violet" title="Every tracker, every casino" note="Deposits, USD. A dashed column is a tracker that does not list that casino." />
            <GlassColumns
              groups={chosen.map((r) => r.name)}
              series={trackers.map((id) => ({ key: id, name: TRACKER_NAMES[id], color: `var(--tracker-${id})`, values: chosen.map((r) => r.by[id]?.value ?? null) }))}
              format={usd}
            />
          </section>
        </>
      )}
    </>
  );
}
